import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { CreateSubscriptionOutput, GetPlanOutput } from "@smash-sdk/billing";
import { Iam } from "@smash-sdk/iam/ejs/10-2019";
import { AsyncState } from '../../shared/models/async.interface';
import { RootState } from '../../store';
import { DisplayUser } from './models/DisplayUser.interface';
import { SignupUser } from './models/SignupUser.interface';
import authService from './services/auth.service';
import permissionService from './services/permission.service';
import { EmailStatus, emailStatuses } from './models/EmailStatus.interface';
import { redirectToAuthPortal } from '../../shared/helpers/authPortal';

const storedUser: string | null = localStorage.getItem('identity');
const identity: DisplayUser | null = !!storedUser ? JSON.parse(storedUser) : null;

const storedEmailStatus: string | null = localStorage.getItem('emailStatus');
const emailStatus: VerificationEmail['emailStatus'] | undefined = !!storedEmailStatus ? JSON.parse(storedEmailStatus).emailStatus : null;

export interface VerificationEmail extends AsyncState {
    emailStatus: EmailStatus;
}
export interface PermissionsState extends AsyncState { }

export interface AuthState extends AsyncState {
    signup: SignupState;
    identity: DisplayUser | null;
    isAuthenticated: boolean;
    permissionsUpdate: PermissionsState;
    verificationEmail: VerificationEmail;
}

export interface SignupState extends AsyncState {
    plan: GetPlanOutput['plan'] | null;
    subscription: CreateSubscriptionOutput['subscription'] | null;
}

export const initialState: AuthState = {
    signup: {
        plan: null,
        subscription: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    identity: identity,
    isAuthenticated: authService.hasActiveSession(),
    permissionsUpdate: {
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    verificationEmail: {
        emailStatus: emailStatus,
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: undefined,
    },
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: undefined,
}

export const getAnonymousToken = createAsyncThunk(
    'auth/getAnonymousToken',
    async (_, thunkApi) => {
        try {
            return await authService.createAnonymousUser();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const signup = createAsyncThunk(
    'auth/signup',
    async (user: SignupUser, thunkApi) => {
        try {
            const { identity, emailStatus, subscription, plan } = await authService.signup(user);
            return { identity, emailStatus, subscription, plan };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const verifyAuthorizationCode = createAsyncThunk(
    'auth/verifyAuthorizationCode',
    async (authorizationCode: string, thunkApi) => {
        try {
            return await authService.verifyAuthorizationCode(authorizationCode);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const logout = createAsyncThunk(
    'auth/logout',
    async () => {
        await authService.logout();
        // Hand back to the auth portal (its session cookie can re-issue a fresh code).
        redirectToAuthPortal();
    }
)

export const verifyJwt = createAsyncThunk(
    'auth/verify-jwt',
    async (_, thunkApi) => {
        try {
            return await authService.verifyJwt();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
);

export const verifyEmail = createAsyncThunk(
    'auth/verifyEmail',
    async (code: number, thunkApi) => {
        try {
            return await authService.verifyEmail(code);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
);

export const sendCode = createAsyncThunk(
    'auth/sendCode',
    async (_, thunkApi) => {
        try {
            return await authService.sendCode();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
);

export const updatePermissions = createAsyncThunk(
    'auth/updatePermissions',
    async (_, thunkApi) => {
        try {
            return await permissionService.updatePermissions();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const setIdentity = createAsyncThunk(
    'auth/setIdentity',
    async (identity: DisplayUser, thunkApi) => {
        authService.setIdentity({ identity });
    }
)

export const updateIdentity = createAsyncThunk(
    'auth/updateIdentity',
    async (_, thunkApi) => {
        try {
            const iam = new Iam();
            const { account } = await iam.getAccount();
            const { auth }: any = thunkApi.getState();
            return {
                identity:
                {
                    ...auth.identity,
                    firstName: account.firstName,
                    lastName: account.lastName,
                    username: account.username
                }
            };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        reset: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.error = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            // UPDATE IDENTITY
            // .addCase(updateIdentity.pending, (state) => { // this cause a full reload of the page
            //     state.isLoading = true;
            // })
            .addCase(updateIdentity.fulfilled, (state, action) => ({
                ...state,
                isLoading: false,
                isSuccess: true,
                identity: action.payload.identity
            }))
            .addCase(updateIdentity.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.identity = null;
            })
            // SIGNUP
            .addCase(signup.pending, (state) => {
                state.signup.isLoading = true;
            })
            .addCase(signup.fulfilled, (state, action) => {
                state.signup.isLoading = false;
                state.signup.isSuccess = true;
                state.signup.plan = action.payload.plan;
                state.signup.subscription = action.payload.subscription;
                state.isAuthenticated = authService.hasActiveSession();
                state.verificationEmail.emailStatus = action.payload.emailStatus;
                state.identity = action.payload.identity;
            })
            .addCase(signup.rejected, (state, action) => {
                state.signup.isLoading = false;
                state.signup.isError = true;
                state.signup.error = action.payload as SerializedError;
                state.identity = null;
                state.isAuthenticated = false;

            })
            // Anonymous
            .addCase(getAnonymousToken.pending, (state) => {
                state.isSuccess = false;
                state.isError = false;
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(getAnonymousToken.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isAuthenticated = false;
                state.identity = action.payload.identity;
            })
            .addCase(getAnonymousToken.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.identity = null;
                state.isAuthenticated = false;
                state.error = action.error;
            })
            // VERIFY AUTHORIZATION CODE (portal login)
            .addCase(verifyAuthorizationCode.pending, (state) => {
                state.isSuccess = false;
                state.isError = false;
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(verifyAuthorizationCode.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.identity = action.payload.identity;
                state.verificationEmail.emailStatus = action.payload.emailStatus;
                state.isAuthenticated = authService.hasActiveSession();
            })
            .addCase(verifyAuthorizationCode.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.identity = null;
                state.isAuthenticated = false;
                state.error = action.payload as SerializedError;
            })
            // UPDATE PERMISSIONS
            .addCase(updatePermissions.pending, (state) => {
                state.permissionsUpdate.isSuccess = false;
                state.permissionsUpdate.isError = false;
                state.permissionsUpdate.isLoading = true;
            })
            .addCase(updatePermissions.fulfilled, (state, action) => {
                state.permissionsUpdate.isSuccess = true;
                state.permissionsUpdate.isError = false;
                state.permissionsUpdate.isLoading = false;
            })
            .addCase(updatePermissions.rejected, (state, action) => {
                state.permissionsUpdate.isSuccess = false;
                state.permissionsUpdate.isError = true;
                state.permissionsUpdate.isLoading = false;
                state.error = action.payload as SerializedError;
            })
            // LOGOUT
            .addCase(logout.pending, (state) => {
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(logout.fulfilled, (state) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.identity = null;
                state.isAuthenticated = false;
            })
            // VERIFY JWT
            .addCase(verifyJwt.pending, (state) => {
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(verifyJwt.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isAuthenticated = action.payload;
            })
            .addCase(verifyJwt.rejected, (state) => {
                state.isLoading = false;
                state.isError = true;
                state.isAuthenticated = false;
            })
            // VERIFY Email
            .addCase(verifyEmail.pending, (state) => {
                state.verificationEmail.isLoading = true;
                state.verificationEmail.error = undefined;
            })
            .addCase(verifyEmail.fulfilled, (state, action) => {
                state.verificationEmail.isLoading = false;
                state.verificationEmail.isSuccess = true;
                state.verificationEmail.emailStatus = action.payload.emailStatus;
                state.verificationEmail.error = undefined;
                state.isAuthenticated = authService.hasActiveSession();
            })
            .addCase(verifyEmail.rejected, (state, action) => {
                state.verificationEmail.isLoading = false;
                state.verificationEmail.isError = true;
                state.verificationEmail.error = action.payload as SerializedError;
                state.isAuthenticated = authService.hasActiveSession();
            })
            // SEND CODE
            .addCase(sendCode.pending, (state) => {
                state.verificationEmail.isSuccess = false;
                state.verificationEmail.isError = false;
                state.verificationEmail.isLoading = true;
                state.verificationEmail.error = undefined;
            })
            .addCase(sendCode.fulfilled, (state, action) => {
                state.verificationEmail.isSuccess = true;
                state.verificationEmail.isError = false;
                state.verificationEmail.isLoading = false;
                state.verificationEmail.error = undefined;
            })
            .addCase(sendCode.rejected, (state, action) => {
                state.verificationEmail.isSuccess = false;
                state.verificationEmail.isLoading = false;
                state.verificationEmail.isError = true;
                state.verificationEmail.error = action.payload as SerializedError;
            })
    }
});

export const { reset } = authSlice.actions;

export const selectedUser = (state: RootState) => state.auth;

export const isAuth = (state: RootState) => state.auth.isAuthenticated;
export const isEmailVerified = (state: RootState) => state.auth.verificationEmail.emailStatus === emailStatuses.Verified;
export const permissionsError = (state: RootState) => state.auth.permissionsUpdate.isError;
export const permissionsUpdated = (state: RootState) => state.auth.permissionsUpdate.isSuccess;
export const permissionsUpdating = (state: RootState) => state.auth.permissionsUpdate.isLoading;

export default authSlice.reducer;